import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LabelList
} from 'recharts';
import { analyticsService } from '../../services/analyticsService';
import { getSchichtType } from '../../utils/schichtType';
import { VERGLEICH_PROJECTS, getProjectVariant } from '../../config/vergleichProjects';

const PROJECTS = VERGLEICH_PROJECTS.map(p => ({
  fullName: p.fullName,
  variant: getProjectVariant(p.fullName),
}));

const emptyAccum = () => ({ A1: 0, A2: 0, A3: 0, A4: 0, qty: 0 });

const addMaterialsToAccum = (accum, materials, { use1990 }) => {
  materials.forEach(material => {
    if (use1990 && !material.has1990Data) return;
    if (!getSchichtType(material)) return;
    const quantity = material.quantity || material.totalQuantity || 1;
    const a1 = use1990 ? (material.A1_1990 || 0) : (material.A1 || 0);
    const a2 = use1990 ? (material.A2_1990 || 0) : (material.A2 || 0);
    const a3 = use1990 ? (material.A3_1990 || 0) : (material.A3 || 0);
    const a4 = use1990 ? (material.A4_1990 || 0) : (material.A4 || 0);
    accum.A1 += a1 * quantity;
    accum.A2 += a2 * quantity;
    accum.A3 += a3 * quantity;
    accum.A4 += a4 * quantity;
    accum.qty += quantity;
  });
};

const finalize = (accum) => {
  if (accum.qty === 0) return { A1: 0, A2: 0, A3: 0, A4: 0, total: 0 };
  const A1 = accum.A1 / accum.qty;
  const A2 = accum.A2 / accum.qty;
  const A3 = accum.A3 / accum.qty;
  const A4 = accum.A4 / accum.qty;
  return { A1, A2, A3, A4, total: A1 + A2 + A3 + A4 };
};

const CustomAxisTick = ({ x, y, payload, data }) => {
  const entry = data.find(d => d.name === payload.value);
  const subtitle = entry?.subtitle;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={12}>
        {payload.value}
      </text>
      {subtitle && (
        <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontSize={12}>
          {subtitle}
        </text>
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  const fmt = (v) => (v || 0).toFixed(1).replace('.', ',');
  return (
    <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
      <p className="font-semibold mb-2">{data.name}</p>
      <div className="space-y-1">
        <p className="text-green-600">A1: {fmt(data.A1)} kg CO₂e/t</p>
        <p className="text-blue-600">A2: {fmt(data.A2)} kg CO₂e/t</p>
        <p className="text-purple-600">A3: {fmt(data.A3)} kg CO₂e/t</p>
        <p className="text-orange-600">A4: {fmt(data.A4)} kg CO₂e/t</p>
        <p className="font-semibold border-t pt-1">Total: {fmt(data.total)} kg CO₂e/t</p>
      </div>
    </div>
  );
};

export const EarningsChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmissionFactors = async () => {
      setLoading(true);
      setError(null);
      try {
        const responses = await Promise.all(
          PROJECTS.map(p =>
            analyticsService.getCO2Breakdown(p.fullName).catch(err => {
              console.warn(`Failed to load CO2 breakdown for ${p.fullName}:`, err);
              return null;
            })
          )
        );

        const acc1990 = emptyAccum();
        const accKonv = emptyAccum();
        const accSoll = emptyAccum();

        responses.forEach((response, idx) => {
          if (!response?.success || !response?.data?.materials) return;
          const { variant } = PROJECTS[idx];
          const materials = response.data.materials;

          addMaterialsToAccum(acc1990, materials, { use1990: true });
          if (variant === 'Konventionell') {
            addMaterialsToAccum(accKonv, materials, { use1990: false });
          } else if (variant === 'CO2 SOLL') {
            addMaterialsToAccum(accSoll, materials, { use1990: false });
          }
        });

        setChartData([
          { name: 'Baumaßnahmen', subtitle: '1990', ...finalize(acc1990) },
          { name: 'Baumaßnahmen konventionell', subtitle: '2021–2023', ...finalize(accKonv) },
          { name: 'Baumaßnahmen mit CO2-Vergabekriterium', subtitle: '2024–2025', ...finalize(accSoll) },
        ]);
      } catch (err) {
        console.error('Error fetching emission factors:', err);
        setError(err.message || 'Failed to load emission factors');
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionFactors();
  }, []);

  return (
    <Card className="h-full flex flex-col">
      <div className="p-6 flex-shrink-0">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h2 className="text-lg font-semibold">Entwicklung durchschnittlicher Emissionsfaktoren von Autobahn Baumaßnahmen</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
              <span className="text-sm text-gray-600">A1 (Rohstoffe)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-400 rounded-sm"></div>
              <span className="text-sm text-gray-600">A2 (Transport)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-purple-400 rounded-sm"></div>
              <span className="text-sm text-gray-600">A3 (Herstellung)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-400 rounded-sm"></div>
              <span className="text-sm text-gray-600">A4 (Transport Baustelle)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 pb-6 min-h-0">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <p>Loading emission factors...</p>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-500">
            {error}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 4 }}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                height={50}
                interval={0}
                tick={<CustomAxisTick data={chartData} />}
              />
              <YAxis
                label={{
                  value: 'kg CO₂e/t',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="A1" stackId="emissions" name="A1 (Rohstoffe)" fill="#4ade80" />
              <Bar dataKey="A2" stackId="emissions" name="A2 (Transport)" fill="#60a5fa" />
              <Bar dataKey="A3" stackId="emissions" name="A3 (Herstellung)" fill="#a78bfa" />
              <Bar
                dataKey="A4"
                stackId="emissions"
                name="A4 (Transport Baustelle)"
                fill="#fb923c"
                radius={[4, 4, 0, 0]}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  formatter={(value) => (value || 0).toFixed(1).replace('.', ',')}
                  style={{ fill: '#374151', fontSize: '14px', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};
